import { useCallback, useMemo, useState } from "react";
import CreateCharacterMpProvider from "@/core/data-provider/data-mp/create-character/create-character-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import useErrorTranslation from "@/core/helpers/useErrorTranslation";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import {
  eyeColorSelector,
  faceSelector,
  geneticsSelector,
  hairSelector,
  hatsSelector,
  headOverlaySelector,
  legsSelector,
  personalDataSelector,
  shoesSelector,
  topsSelector,
} from "@/domains/createCharacter/slice";
import toast from "@/domains/toasts/helpers/toast";

export default function useCreateCharacter() {
  const tError = useErrorTranslation();
  const dispatch = useAppDispatch();
  const [isPending, setIsPending] = useState(false);
  const [isCompleted, setIsCompleted] = useState<null | boolean>(null);
  const [error, setError] = useState("");

  const personalData = useAppSelector(personalDataSelector);
  const genetics = useAppSelector(geneticsSelector);
  const hair = useAppSelector(hairSelector);
  const headOverlay = useAppSelector(headOverlaySelector);
  const face = useAppSelector(faceSelector);
  const eyeColor = useAppSelector(eyeColorSelector);
  const hats = useAppSelector(hatsSelector);
  const tops = useAppSelector(topsSelector);
  const legs = useAppSelector(legsSelector);
  const shoes = useAppSelector(shoesSelector);

  const characterData = useMemo(() => {
    return {
      personalInformation: personalData,
      hair: {
        drawableId: hair.drawableId,
        color: hair.color,
        highlightColor: hair.highlightColor,
      },
      face: {
        eyes: face.eyes,
        eyeColor,
        nose: {
          width: face.noseWidth,
          height: face.noseHeight,
          length: face.noseLength,
          bridge: face.noseBridge,
          tip: face.noseTip,
          bridgeShift: face.noseBridgeShift,
        },
        lips: face.lips,
        brow: {
          height: face.browHeight,
          width: face.browWidth,
        },
        cheeks: {
          width: face.cheeksWidth,
        },
        cheekbone: {
          height: face.cheekboneHeight,
          width: face.cheekboneWidth,
        },
        jaw: {
          width: face.jawWidth,
          height: face.jawHeight,
        },
        chin: {
          length: face.chinLength,
          position: face.chinPosition,
          width: face.chinWidth,
          shape: face.chinShape,
        },
        neck: {
          width: face.neckWidth,
        },
        blemishes: {
          index: headOverlay.blemishes.index,
          opacity: headOverlay.blemishes.opacity,
          firstColor: headOverlay.blemishes.firstColor,
          secondColor: headOverlay.blemishes.secondColor,
        },
        facialHair: {
          index: headOverlay.facialHair.index,
          opacity: headOverlay.facialHair.opacity,
          firstColor: headOverlay.facialHair.firstColor,
          secondColor: headOverlay.facialHair.secondColor,
        },
        eyebrows: {
          index: headOverlay.eyebrows.index,
          opacity: headOverlay.eyebrows.opacity,
          firstColor: headOverlay.eyebrows.firstColor,
          secondColor: headOverlay.eyebrows.secondColor,
        },
        ageing: {
          index: headOverlay.ageing.index,
          opacity: headOverlay.ageing.opacity,
          firstColor: headOverlay.ageing.firstColor,
          secondColor: headOverlay.ageing.secondColor,
        },
        makeup: {
          index: headOverlay.makeup.index,
          opacity: headOverlay.makeup.opacity,
          firstColor: headOverlay.makeup.firstColor,
          secondColor: headOverlay.makeup.secondColor,
        },
        blush: {
          index: headOverlay.blush.index,
          opacity: headOverlay.blush.opacity,
          firstColor: headOverlay.blush.firstColor,
          secondColor: headOverlay.blush.secondColor,
        },
        complexion: {
          index: headOverlay.complexion.index,
          opacity: headOverlay.complexion.opacity,
          firstColor: headOverlay.complexion.firstColor,
          secondColor: headOverlay.complexion.secondColor,
        },
        sunDamage: {
          index: headOverlay.sunDamage.index,
          opacity: headOverlay.sunDamage.opacity,
          firstColor: headOverlay.sunDamage.firstColor,
          secondColor: headOverlay.sunDamage.secondColor,
        },
        lipstick: {
          index: headOverlay.lipstick.index,
          opacity: headOverlay.lipstick.opacity,
          firstColor: headOverlay.lipstick.firstColor,
          secondColor: headOverlay.lipstick.secondColor,
        },
        molesFreckles: {
          index: headOverlay.molesFreckles.index,
          opacity: headOverlay.molesFreckles.opacity,
          firstColor: headOverlay.molesFreckles.firstColor,
          secondColor: headOverlay.molesFreckles.secondColor,
        },
        chestHair: {
          index: headOverlay.chestHair.index,
          opacity: headOverlay.chestHair.opacity,
          firstColor: headOverlay.chestHair.firstColor,
          secondColor: headOverlay.chestHair.secondColor,
        },
        bodyBlemishes: {
          index: headOverlay.bodyBlemishes.index,
          opacity: headOverlay.bodyBlemishes.opacity,
          firstColor: headOverlay.bodyBlemishes.firstColor,
          secondColor: headOverlay.bodyBlemishes.secondColor,
        },
        addBodyBlemishes: {
          index: headOverlay.addBodyBlemishes.index,
          opacity: headOverlay.addBodyBlemishes.opacity,
          firstColor: headOverlay.addBodyBlemishes.firstColor,
          secondColor: headOverlay.addBodyBlemishes.secondColor,
        },
      },
      headBlend: {
        father: genetics.father,
        mother: genetics.mother,
        similarity: genetics.similarity,
        skin: genetics.skin,
      },
      headOverlay,
      props: {
        hats: {
          propId: hats.propId,
          drawableId: hats.drawableId,
          textureId: hats.textureId,
        },
      },
      clothes: {
        tops: {
          componentId: tops.componentId,
          drawableId: tops.drawableId,
          textureId: tops.textureId,
          paletteId: tops.paletteId,
        },
        legs: {
          componentId: legs.componentId,
          drawableId: legs.drawableId,
          textureId: legs.textureId,
          paletteId: legs.paletteId,
        },
        shoes: {
          componentId: shoes.componentId,
          drawableId: shoes.drawableId,
          textureId: shoes.textureId,
          paletteId: shoes.paletteId,
        },
      },
    };
  }, [genetics, face, hair, personalData, headOverlay, eyeColor, tops, legs, shoes, hats]);

  const createCharacter = useCallback(async () => {
    try {
      setIsPending(true);

      const response = await CreateCharacterMpProvider.createCharacter(characterData);

      if (response instanceof RpcMpProviderSuccessResponse) {
        setIsCompleted(true);
      } else {
        setIsCompleted(false);
        setError(tError(response.message));
        toast.game.warning(tError(response.message));
      }
    } catch (e) {
      setIsCompleted(false);
      setError(tError(e.message));
      toast.game.warning(tError(e.message));
    } finally {
      setIsPending(false);
    }
  }, [dispatch, characterData]);

  const initCharacter = useCallback(async () => {
    try {
      setIsPending(true);

      const response = await CreateCharacterMpProvider.setHeadBlend({
        father: characterData.headBlend.father,
        mother: characterData.headBlend.mother,
        similarity: characterData.headBlend.similarity,
        skin: characterData.headBlend.skin,
      });

      if (response instanceof RpcMpProviderSuccessResponse) {
        setIsCompleted(true);
      } else {
        setIsCompleted(false);
        setError(tError(response.message));
        toast.game.warning(tError(response.message));
      }
    } catch (e) {
      setIsCompleted(false);
      setError(tError(e.message));
      toast.game.warning(tError(e.message));
    } finally {
      setIsPending(false);
    }
  }, [dispatch, characterData]);

  return { createCharacter, initCharacter, isPending, isCompleted, error };
}
